import {IconProp} from '@fortawesome/fontawesome-svg-core';
import SidebarButton from 'components/SidebarButton';
import useCompositionNavegation from 'features/useCompositionNavegation';
import {useEffect, useState} from 'react';
import {connect} from 'react-redux';
import {
  changeToggleCompletedTestReportStatus,
  getToggleStatus,
  SelectedToggle,
} from 'store/reducers/toggle';
import {ApplicationState} from 'store/rootReducer';
import useIndexedDB from 'utils/hooks/useIndexedDB';
import useTrackEvent from 'utils/hooks/useTrackEvent';
import {EventData} from 'utils/hooks/useTrackEvent/types';
import {CompositionDetailExtended} from 'utils/types/compositions';
import {WritePreparation} from 'APIs/letrusAPI/writePreparation/types';
import styles from './SidebarButtons.module.scss';

interface OwnProps {
  eventData?: EventData;
  composition: ImmutableMap<CompositionDetailExtended>;
  allowManuscriptSubmission?: boolean | null;
}

const HIGH_SCHOOL_GRID_IDS = [1, 2, 5, 7, 59];

interface StateProps {
  toggleStatus: SelectedToggle;
}

interface DispatchProps {
  changeToggleCompletedTestReportStatus: typeof changeToggleCompletedTestReportStatus;
}

export type SidebarButtonsProps = StateProps & DispatchProps & OwnProps;

function SidebarButtons({
  eventData,
  composition,
  toggleStatus,
  changeToggleCompletedTestReportStatus,
  allowManuscriptSubmission,
}: SidebarButtonsProps): JSX.Element {
  const [hasLocalWritePreparation, setLocalWritePreparation] =
    useState<WritePreparation>();

  const {trackEvent} = useTrackEvent();
  const {toggleWriteToReWrite} = useCompositionNavegation({composition});
  const writePreparationIDB = useIndexedDB<WritePreparation>(
    'LocalWritePreparations',
  );

  const parentId = composition.get('parent_id');
  const childId = composition.get('child_id');

  const reviewGridId = composition?.getIn([
    'instruction_id',
    'genre',
    'review_grid',
  ]);
  const reviewType = composition?.get('review_type');

  const compositionStatus = composition.get('status') ?? 'writing';
  const hasWritingPreparation = composition.get('uses_write_preparation');

  const isFundII =
    composition?.getIn(['instruction_id', 'genre', 'review_grid_name']) ===
    'Grade Única';
  const isDynamicEditor =
    composition.getIn(['test', 'template', 'editor']) === 'dynamic';
  const isReviewGridMultigenre =
    composition.getIn(['instruction_id', 'genre', 'review_grid']) === 61;
  const isMultigenreRewriting =
    isReviewGridMultigenre && !!parentId && !!hasLocalWritePreparation;

  useEffect(() => {
    writePreparationIDB
      .getItem(String(parentId))
      .then((localWritePreparation) => {
        if (localWritePreparation) {
          setLocalWritePreparation(localWritePreparation);
        }
      });
  }, []);

  function handleClick(status: SelectedToggle) {
    if (status === 'annotations' && eventData) {
      trackEvent('selected_annotation_tabs', eventData);
    }
    if (status === 'last-review' && eventData) {
      trackEvent('test_group_sidebar_2', eventData);
    }

    if (toggleStatus === status) {
      changeToggleCompletedTestReportStatus(null);
      return;
    }

    changeToggleCompletedTestReportStatus(status);
  }

  function handleSwitchClick() {
    if (isReviewGridMultigenre && (parentId || childId)) {
      toggleWriteToReWrite?.({hasRewriting: true});
    } else {
      toggleWriteToReWrite?.();
    }
  }

  function getWritingAndRewritingButtonText() {
    if (childId) {
      if (isReviewGridMultigenre) {
        return 'Continuar reescrita';
      }
      return 'Reescrita';
    }

    if (parentId) {
      if (isReviewGridMultigenre) {
        return 'Ver escrita';
      }

      return 'Escrita';
    }
  }

  interface ButtonProps {
    icon?: IconProp;
    onClick?(): void;
    text?: string;
    status?: SelectedToggle;
    htmlFor: string;
    disabled?: boolean;
    hide?: boolean;
  }

  function getButtonsData(): ButtonProps[] {
    const isMultiGrid = reviewGridId === 59;
    const isCompetencesReviewType =
      reviewType === 'AI-Competences' || reviewType === 'Human';

    const isCompetencesReviewGrid =
      HIGH_SCHOOL_GRID_IDS.includes(reviewGridId || -1) || !!isFundII;

    const isCompetenceButtonAvailable =
      isMultiGrid || (isCompetencesReviewGrid && isCompetencesReviewType);

    const basicButtons: ButtonProps[] = [
      {
        icon: 'cloud-upload',
        onClick: () => handleClick('OCR'),
        text: 'Enviar Manuscrito',
        htmlFor: 'OCR',
        status: 'OCR',
        hide: allowManuscriptSubmission === false,
      },
      {
        icon: 'file-invoice',
        onClick: () => handleClick('proposal'),
        text: 'Proposta',
        htmlFor: 'proposal',
        status: 'proposal',
      },
      {
        icon: isFundII ? 'trophy-alt' : 'bullseye-arrow',
        onClick: () => handleClick(isFundII ? 'correction' : 'structure'),
        text: isFundII ? 'Correção' : 'Desempenho',
        disabled: !composition.get('flag_finished') && !isDynamicEditor,
        status: isFundII ? 'correction' : 'structure',
        htmlFor: isFundII ? 'correction' : 'structure',
        hide: isReviewGridMultigenre && !!parentId,
      },
    ];

    const competenceButton: ButtonProps = isMultiGrid
      ? {
          icon: 'chart-pie',
          onClick: () => handleClick('performance'),
          text: 'Resumo',
          disabled:
            compositionStatus === 'writing' ||
            compositionStatus === 'preparing',
          htmlFor: 'performance',
          status: 'performance',
        }
      : {
          icon: isFundII ? 'envelope' : 'chart-pie',
          onClick: () => handleClick(isFundII ? 'next-steps' : 'competencies'),
          text: isFundII ? 'Próximos passos' : 'Competências',
          disabled:
            compositionStatus === 'writing' ||
            compositionStatus === 'preparing',
          htmlFor: isFundII ? 'next-steps' : 'competencies',
          status: 'competencies',
        };

    if (hasWritingPreparation || isMultigenreRewriting) {
      basicButtons.splice(1, 0, {
        icon: 'edit',
        onClick: () => handleClick('annotations'),
        text: 'Anotações',
        htmlFor: 'annotations',
        status: 'annotations',
      });
    }

    if (parentId || childId) {
      basicButtons.splice(2, 0, {
        icon: 'pen-square',
        onClick: handleSwitchClick,
        text: getWritingAndRewritingButtonText(),
        disabled: !childId && !parentId,
        htmlFor: 'rewriting',
      });
    }

    if (isCompetenceButtonAvailable) {
      return [...basicButtons, competenceButton];
    }

    return basicButtons;
  }

  return (
    <section className={styles.wrapper}>
      {getButtonsData().map(
        ({htmlFor, onClick, status, icon, text, disabled, hide}) => (
          <SidebarButton
            htmlFor={htmlFor}
            onClick={onClick}
            icon={icon}
            text={text}
            disabled={disabled}
            hide={hide}
            key={htmlFor}
            ariaExpanded={toggleStatus === status}
            active={toggleStatus === status}
          />
        ),
      )}
    </section>
  );
}

export default connect(
  (state: ApplicationState) => ({
    toggleStatus: getToggleStatus(state),
  }),
  {
    changeToggleCompletedTestReportStatus,
  },
)(SidebarButtons);
